<template>
  <div id="app" class="container" v-if="hydrated">
    <template v-if="isMarketingRoute">
      <router-view />
    </template>
    <template v-else>
      <Header :showHeader="showHeader" :showTitle="showTitle" />
      <div class="mobile-topbar" v-if="showLeftNav">
        <button class="mobile-hamburger" @click="mobileNavOpen = true" aria-label="Open menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <span class="mobile-topbar-title">EyeJack Creator</span>
      </div>
      <div :class="['routerContainer', showTopMargin ? 'topMargin' : '']">
        <left-nav v-if="showLeftNav" :class="{ 'mobile-open': mobileNavOpen }" />
        <div v-if="showLeftNav && mobileNavOpen" class="nav-backdrop" @click="mobileNavOpen = false"></div>
        <keep-alive include="artworks-page">
          <router-view />
        </keep-alive>
      </div>
      <transition name="fade">
        <Overlay v-if="!online" message="No internet connection." />
      </transition>
      <transition name="fade">
        <Overlay v-if="isDialogOpen" message="File dialog open..." />
      </transition>
    </template>
    <!-- Global "best on desktop" overlay — shown instantly when a touch user taps a create action,
         so we never navigate into (and lazily load) the desktop-only creation/editor routes. -->
    <desktop-only-notice v-if="showDesktopNotice" back-label="Back" @back="showDesktopNotice = false" />
  </div>
</template>

<script>
import { components } from 'aws-amplify-vue';

import Header from '@/components/Header';
import LeftNav from '@/components/LeftNav';
import Overlay from '@/components/Overlay';
import DesktopOnlyNotice from '@/components/DesktopOnlyNotice';

import OnUpdateArtwork from '@/subscriptions/OnUpdateArtwork';
import OnUpdateCollection from '@/subscriptions/OnUpdateCollection';
import GetPaymentPlans from '@/queries/GetPaymentPlans';
import GetUpcomingInvoice from '@/queries/GetUpcomingInvoice';
import GetUpcomingInvoiceItems from '@/queries/GetUpcomingInvoiceItems';

export default {
  name: 'App',
  data() {
    return {
      hydrated: false,
      online: true,
      mobileNavOpen: false,
      showDesktopNotice: false,
    };
  },
  watch: {
    // Close the mobile nav drawer whenever the route changes (e.g. tapping a nav item).
    $route() {
      this.mobileNavOpen = false;
    },
  },
  components: {
    Header,
    LeftNav,
    Overlay,
    DesktopOnlyNotice,
    ...components,
  },
  apollo: {
    getPaymentPlans: {
      query: GetPaymentPlans,
      fetchPolicy: 'no-cache',
      result({ data }) {
        if (!data || !data.getPaymentPlans) return;
        const freePlan = data.getPaymentPlans.find((plan) => plan.nickname === 'Free Monthly USD');
        const artistPlan = data.getPaymentPlans.find((plan) => plan.nickname === 'Artist Monthly USD');
        const virtuosoPlan = data.getPaymentPlans.find((plan) => plan.nickname === 'Virtuoso Monthly USD');

        if (!freePlan || !artistPlan || !virtuosoPlan) return;

        const freeMeta = JSON.parse(freePlan.metadata);
        const artistMeta = JSON.parse(artistPlan.metadata);
        const virtuosoMeta = JSON.parse(virtuosoPlan.metadata);

        const payload = {
          freeViewLimit: parseInt(freeMeta.views, 10),
          freePanelsPublishLimit: parseInt(freeMeta.panels, 10),
          freePanelsSizeLimit: parseInt(freeMeta.panelsSize, 10),
          freeArtworkDurationLimit: parseInt(freeMeta.duration, 10),
          artistViewLimit: parseInt(artistMeta.views, 10),
          artistPanelsPublishLimit: parseInt(artistMeta.panels, 10),
          artistPanelsSizeLimit: parseInt(artistMeta.panelsSize, 10),
          artistArtworkDurationLimit: parseInt(artistMeta.duration, 10),
          virtuosoViewLimit: parseInt(virtuosoMeta.views, 10),
          virtuosoPanelsPublishLimit: parseInt(virtuosoMeta.panels, 10),
          virtuosoPanelsSizeLimit: parseInt(virtuosoMeta.panelsSize, 10),
          virtuosoArtworkDurationLimit: parseInt(virtuosoMeta.duration, 10),
        };

        this.$store.commit('SET_PLAN_SETTINGS', payload);
      },
      skip() {
        return !this.$store.getters.signedIn;
      },
    },
    getUpcomingInvoice: {
      query: GetUpcomingInvoice,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          customer_id: this.$store.getters.userDetails.stripeid,
          subscription_id: this.$store.getters.userDetails.subscriptionid,
        };
      },
      result({ data }) {
        this.$store.commit('SET_USAGE', {
          invoice: data.getUpcomingInvoice,
        });
      },
      skip() {
        return !(this.$store.getters.userDetails.stripeid && this.$store.getters.userDetails.subscriptionid);
      },
    },
    getUpcomingInvoiceItems: {
      query: GetUpcomingInvoiceItems,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          customer_id: this.$store.getters.userDetails.stripeid,
          subscription_id: this.$store.getters.userDetails.subscriptionid,
        };
      },
      result({ data }) {
        if (data.getUpcomingInvoiceItems !== null) {
          this.$store.commit('SET_USAGE', {
            invoiceItems: data.getUpcomingInvoiceItems.data,
          });
        }
      },
      skip() {
        return !(this.$store.getters.userDetails.stripeid && this.$store.getters.userDetails.subscriptionid);
      },
    },
    $subscribe: {
      onUpdateArtwork: {
        query: OnUpdateArtwork,
        variables() {
          return {
            userid: this.$store.getters.userId,
          };
        },
        result({ data }) {
          if (!data || !data.onUpdateArtwork) return;
          this.$root.$emit('artwork_update', data.onUpdateArtwork);
          this.$store.commit('UPDATE_ARTWORK_FIELDS', {
            id: data.onUpdateArtwork.id,
            viewsCount: data.onUpdateArtwork.viewsCount,
            downloadsCount: data.onUpdateArtwork.downloadsCount,
            name: data.onUpdateArtwork.name,
            description: data.onUpdateArtwork.description,
            status: data.onUpdateArtwork.status,
            publishEnabled: data.onUpdateArtwork.publishEnabled,
          });
        },
        error(error) {
          // Silently handle subscription errors (e.g., MQTT connection timeouts)
          // These are typically network/connectivity issues and don't affect core functionality
          console.warn('[App] Artwork subscription error:', error?.errorMessage || error);
        },
        skip() {
          return !this.$store.getters.signedIn;
        },
      },
      onUpdateCollection: {
        query: OnUpdateCollection,
        variables() {
          return {
            userid: this.$store.getters.userId,
          };
        },
        result({ data }) {
          if (!data || !data.onUpdateCollection) return;
          this.$root.$emit('collection_update', data.onUpdateCollection);
          this.$store.commit('UPDATE_COLLECTION_FIELDS', {
            id: data.onUpdateCollection.id,
            viewsCount: data.onUpdateCollection.viewsCount,
            downloadsCount: data.onUpdateCollection.downloadsCount,
            title: data.onUpdateCollection.title,
            description: data.onUpdateCollection.description,
            status: data.onUpdateCollection.status,
            publishEnabled: data.onUpdateCollection.publishEnabled,
          });
        },
        error(error) {
          // Silently handle subscription errors (e.g., MQTT connection timeouts)
          console.warn('[App] Collection subscription error:', error?.errorMessage || error);
        },
        skip() {
          return !this.$store.getters.signedIn;
        },
      },
    },
  },
  computed: {
    isMarketingRoute() {
      const path = this.$route?.path || '';
      const name = this.$route?.name || '';
      return (
        name === 'marketing-page' ||
        path === '/' ||
        path.startsWith('/landing/')
      );
    },
    showTopMargin() {
      // Browser app: no native titlebar, keep margin off.
      return false;
    },
    showHeader() {
      return (
        this.$store.getters.currentStep.name === 'animation' ||
        this.$store.getters.currentStep.name === 'artwork'
      );
    },
    showTitle() {
      return false;
    },
    showLeftNav() {
      return this.$store.getters.currentStep.index === 0;
    },
    isDialogOpen() {
      return this.$store.getters.isDialogOpen;
    },
  },
  async mounted() {
    try {
      await this.$apollo.provider.defaultClient.hydrated();
    } catch (e) {
      // Ignore hydration errors when user is not logged in
      console.debug('Apollo hydration skipped (no user session)');
    }
    this.hydrated = true;

    this.online = navigator.onLine;

    const currentPath = this.$route?.path || '';
    const currentName = this.$route?.name || '';
    const isPublicMarketingRoute =
      currentPath === '/' ||
      currentPath.startsWith('/landing/') ||
      currentName === 'marketing-page';

    if (!this.$store.getters.userId && currentName !== 'login-page' && !isPublicMarketingRoute) {
      this.$router.push({ name: 'login-page' });
    }

    window.addEventListener('offline', () => {
      this.online = false;
    });

    window.addEventListener('online', () => {
      this.online = true;
    });

    // Touch users tapping a desktop-only create action ask us (via the root event bus) to show
    // the notice immediately, instead of routing into the heavy creation/editor pages.
    this.$root.$on('show-desktop-notice', () => {
      this.showDesktopNotice = true;
    });
  },
};
</script>

<style lang="stylus">
@import './styles/main.styl'

#app
  -webkit-font-smoothing antialiased
  -moz-osx-font-smoothing grayscale
  color #2c3e50

.routerContainer
  height: 100%
  display: flex

// Mobile: a slim top bar with a hamburger opens the LeftNav as a slide-in drawer.
// Hidden on desktop, where the 150px rail stays exactly as it was.
.mobile-topbar
  display: none

@media (max-width: mobile-breakpoint)
  .mobile-topbar
    display: flex
    align-items: center
    gap: 12px
    min-height: 56px
    flex-shrink: 0
    // Extend the bar under the iOS notch (needs viewport-fit=cover; env() is 0 elsewhere).
    padding: env(safe-area-inset-top) 16px 0 16px
    background: primary-color-red
    color: white
    box-shadow: 0 0 shadow-y-height 0 color-shadow
    z-index: $navbar-z-index

  .mobile-hamburger
    display: flex
    align-items: center
    justify-content: center
    background: none
    border: none
    color: white
    padding: 8px
    margin-left: -8px
    cursor: pointer

  .mobile-topbar-title
    font-size: 16px
    font-weight: 600

  // Let the page fill the space under the top bar instead of overflowing (desktop keeps height:100%).
  // Also make this the single vertical scroll viewport on mobile: html/body are overflow:hidden and
  // no inner element scrolls, so tall stacked pages (profile/plan/detail) would otherwise be clipped.
  // Grid pages keep their own internal .artwork-grid scroll — they fit exactly, so this never engages there.
  //
  // Deliberately NOT using -webkit-overflow-scrolling:touch: on iOS Safari it makes this element the
  // containing block for position:fixed descendants, which trapped the nav drawer + backdrop *inside*
  // this box (they resolved top:0 to here, below the top bar) instead of the viewport — so the bar sat
  // on top of the drawer. iOS 13+ already has momentum scrolling by default, so it buys us nothing.
  .routerContainer
    flex: 1 1 auto
    height: auto
    min-height: 0
    overflow-y: auto

  .nav-backdrop
    position: fixed
    top: 0
    left: 0
    right: 0
    bottom: 0
    background: rgba(0, 0, 0, 0.45)
    z-index: $modal-z-index - 1

</style>
